import { ReactElement } from 'react'
import { buildErrorMessage } from '@constants'
import { useIsOrganisationAdmin } from '@hooks'
import { AxiosError } from 'axios'
import Delete from '@mui/icons-material/Delete'
import Upload from '@mui/icons-material/Upload'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import DownLoadIconButton from '@ui/actions/DownLoadIconButton'
import { LoadingIndicator } from '@ui/feedback'
import { useSnackbar } from '@ui/snackbar'
import { getCustomerFiles, getDefaultDocumentationTemplate } from '@queries'
import { downLoadFile } from '@utils'
import { deleteCustomerFile, saveCustomerFile, updateCustomerFileContent } from '@mutations'
import downloadCustomerFile from '../../downloadCustomerFile'

const documentationTemplateKind = 'documentationTemplate'
const docxMimeType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'

const documentTypes: DocumentationTemplateType[] = ['coverPage', 'headerFooter', 'tableOfContents']

const documentTypeLabels: Record<DocumentationTemplateType, string> = {
  coverPage: 'Deckblatt',
  headerFooter: 'Erste Seite (Header/Footer)',
  tableOfContents: 'Inhaltsverzeichnis',
}

const templateFileName = (documentType: DocumentationTemplateType) =>
  `Vorlage ${documentTypeLabels[documentType]}`

const DocumentationTemplatesSection = (): ReactElement => {
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()
  // A member who does not administer the organisation may download the
  // templates but not replace them.
  const { isOrganisationAdmin } = useIsOrganisationAdmin()
  const readOnly = !isOrganisationAdmin

  const templatesQueryKey = getCustomerFiles.getKey(documentationTemplateKind)

  const { data: templatesResult, isLoading: isLoadingTemplates } = useQuery<
    CustomerFilesResult<DocumentationTemplateAdditionalData>
  >(templatesQueryKey, () => getCustomerFiles.request(documentationTemplateKind), {
    onError: (error: unknown) => {
      enqueueSnackbar(
        buildErrorMessage(error as AxiosError, 'Fehler beim Laden der Dokumentationsvorlagen'),
        {
          variant: 'error',
        },
      )
    },
  })

  const findCustomTemplate = (documentType: DocumentationTemplateType) =>
    [...(templatesResult?.files || [])]
      .filter(file => file.additional_data?.documentType === documentType)
      .sort((a, b) => (a.created || '').localeCompare(b.created || ''))
      .pop()

  const {
    mutate: downloadDefaultTemplate,
    isLoading: isDownloadingDefault,
    variables: downloadVariables,
  } = useMutation(
    async (documentType: DocumentationTemplateType) => {
      const data = await getDefaultDocumentationTemplate.request(documentType)
      return { data, documentType }
    },
    {
      onSuccess: ({ data, documentType }) => {
        downLoadFile(data, docxMimeType, `${templateFileName(documentType)}.docx`)
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Herunterladen der Standardvorlage'), {
          variant: 'error',
        })
      },
    },
  )

  const {
    mutate: downloadCustomTemplate,
    isLoading: isDownloadingCustom,
    variables: downloadCustomVariables,
  } = useMutation(downloadCustomerFile, {
    onError: (error: Error) => {
      enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Herunterladen der eigenen Vorlage'), {
        variant: 'error',
      })
    },
  })

  const {
    mutate: uploadTemplate,
    isLoading: isUploading,
    variables: uploadVariables,
  } = useMutation(
    async ({ documentType, file }: { documentType: DocumentationTemplateType; file: File }) => {
      const existingTemplate = findCustomTemplate(documentType)

      if (existingTemplate) {
        return updateCustomerFileContent.request(existingTemplate.guid, file)
      }

      const additionalData: DocumentationTemplateAdditionalData = {
        kind: documentationTemplateKind,
        documentType,
      }

      return saveCustomerFile.request({
        name: templateFileName(documentType),
        additional_data: additionalData,
        file,
      })
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(templatesQueryKey)
        enqueueSnackbar('Dokumentationsvorlage erfolgreich hochgeladen', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(
          buildErrorMessage(error, 'Fehler beim Hochladen der Dokumentationsvorlage'),
          {
            variant: 'error',
          },
        )
      },
    },
  )

  const { mutate: deleteTemplate, isLoading: isDeleting } = useMutation(
    ({
      customTemplate,
    }: {
      documentType: DocumentationTemplateType
      customTemplate: CustomerFile<DocumentationTemplateAdditionalData>
    }) => deleteCustomerFile.request(customTemplate.guid),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(templatesQueryKey)
        enqueueSnackbar('Eigene Dokumentationsvorlage entfernt', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(
          buildErrorMessage(error, 'Fehler beim Entfernen der Dokumentationsvorlage'),
          {
            variant: 'error',
          },
        )
      },
    },
  )

  return (
    <Box>
      <Typography variant="h6" mb={2}>
        Dokumentation
      </Typography>
      <TableContainer component={Paper}>
        {isLoadingTemplates ? (
          <Box p={2}>
            <LoadingIndicator />
          </Box>
        ) : (
          <Table size="small" data-cy="documentation-template-table">
            <TableHead>
              <TableRow>
                <TableCell>Dokument</TableCell>
                <TableCell align="center">Vorlage</TableCell>
                <TableCell align="center">Standardvorlage</TableCell>
                <TableCell align="center">Eigene Vorlage</TableCell>
                <TableCell align="center">Neue Vorlage hochladen</TableCell>
                <TableCell align="center">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documentTypes.map(documentType => {
                const customTemplate = findCustomTemplate(documentType)

                return (
                  <TableRow
                    key={documentType}
                    data-cy={`documentation-template-row-${documentType}`}
                  >
                    <TableCell>{documentTypeLabels[documentType]}</TableCell>
                    <TableCell align="center">
                      {customTemplate ? (
                        <Chip label="Angepasst" color="primary" size="small" />
                      ) : (
                        <Chip label="Standard" size="small" />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Standardvorlage herunterladen">
                        <span>
                          <DownLoadIconButton
                            size="small"
                            sx={{ color: 'action.active' }}
                            loading={isDownloadingDefault && downloadVariables === documentType}
                            onClick={() => downloadDefaultTemplate(documentType)}
                            data-cy={`download-default-documentation-template-${documentType}`}
                          />
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      {customTemplate?.url ? (
                        <Tooltip title="Eigene Vorlage herunterladen">
                          <span>
                            <DownLoadIconButton
                              size="small"
                              sx={{ color: 'action.active' }}
                              loading={
                                isDownloadingCustom &&
                                downloadCustomVariables?.guid === customTemplate.guid
                              }
                              onClick={() => downloadCustomTemplate(customTemplate)}
                              data-cy={`download-custom-documentation-template-${documentType}`}
                            />
                          </span>
                        </Tooltip>
                      ) : (
                        '–'
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {!readOnly && (
                        <LoadingButton
                          component="label"
                          size="small"
                          startIcon={<Upload fontSize="small" />}
                          loading={isUploading && uploadVariables?.documentType === documentType}
                          data-cy={`upload-documentation-template-${documentType}`}
                        >
                          Hochladen
                          <input
                            hidden
                            type="file"
                            accept={`.docx,${docxMimeType}`}
                            onChange={event => {
                              const file = event.target.files?.[0]
                              if (file) uploadTemplate({ documentType, file })
                              event.target.value = ''
                            }}
                          />
                        </LoadingButton>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {customTemplate && !readOnly && (
                        <Tooltip title="Eigene Vorlage entfernen">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => deleteTemplate({ documentType, customTemplate })}
                              disabled={isDeleting}
                              data-cy={`delete-documentation-template-${documentType}`}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Box>
  )
}

export default DocumentationTemplatesSection
